import React, { Fragment, useEffect, type FC } from 'react';
// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions
import { useQuery } from 'react-apollo';
import { css } from '@compiled/react';

import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { ContentUnifiedQuery, ContentBlogUnifiedQuery } from '@confluence/content-unified-query';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { useContentType } from '@confluence/page-context';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import {
	useViewPageDistinctionBannerStore,
	VIEW_PAGE_DISTINCTION_BANNER_HEIGHT,
} from '@confluence/live-pages-features/entry-points/ViewPageDistinctionBanner.ts';
import { HighlightActionsProviderForNav4 } from '@confluence/highlight-actions/entry-points/HighlightActionsProvider.ts';
import {
	useObjectSidebarActions,
	useIsObjectSidebarShown,
	useBehavior,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { ObjectSidebarPanel } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarPanelWrapper.tsx';
import { ObjectSidebarPushPlaceholder } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarPushPlaceholder.tsx';
import { fg } from '@confluence/feature-gating';
import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';

import { ContentScreenComponent } from './ContentScreenComponent.tsx';
import { ContentContainerComponent } from './ContentContainerComponent.js';

const EMPTY_CONTENT_SCREEN_STYLES = {};
const EMPTY_LOOK_AND_FEEL = {};

const contentWrapperStyles = css({
	display: 'block',
	flex: '2',
	width: '100%',
});

type ContentContainerProps = {
	contentId: string;
	fetchTheme?: boolean;
	children:
		| React.ReactNode
		| ((props: { isThemed: boolean; isLoading: boolean }) => React.ReactNode);
	isScreenContentView?: boolean;
	isContainerContentView?: boolean;
	spaceKey?: string;
	errorAttribution: string;
	contentScreenStyles?: any;
	stickyHeader?: React.ReactNode;
	versionOverride?: number | null;
};

export const ContentContainer: FC<ContentContainerProps> = ({
	contentId,
	fetchTheme = false,
	children,
	isScreenContentView = false,
	isContainerContentView = false,
	spaceKey,
	errorAttribution = Attribution.BACKBONE as string,
	contentScreenStyles = EMPTY_CONTENT_SCREEN_STYLES,
	stickyHeader = null,
	versionOverride = null,
}) => {
	const isLivePage = useIsCurrentPageLive();
	const [contentType] = useContentType();
	const { hideObjectSidebar } = useObjectSidebarActions();
	const isObjectSidebarShown = useIsObjectSidebarShown();
	const behavior = useBehavior();
	const pageMode = useGetPageMode();

	const [{ isViewPageBannerVisible }] = useViewPageDistinctionBannerStore();
	const isRovoButtonEnabled = useAIRovoButtonEnablement();

	// Close the object sidebar when navigating to a new page
	useEffect(() => {
		hideObjectSidebar();
	}, [contentId, hideObjectSidebar]);

	const chooseContentUnifiedQuery =
		contentType === 'blogpost' ? ContentBlogUnifiedQuery : ContentUnifiedQuery;

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const queryResult = useQuery(chooseContentUnifiedQuery, {
		context: { single: true },
		skip: !fetchTheme || !contentId,
		variables: {
			contentId,
			spaceKey,
			versionOverride,
			useNewContentTopper: true,
		},
	});

	if (queryResult.error) {
		return (
			<ErrorBoundary attribution={errorAttribution}>
				{children as React.ReactNode}
				<ErrorDisplay error={queryResult.error} />
			</ErrorBoundary>
		);
	} else {
		const theme = Boolean(queryResult?.data?.space?.theme);
		const lookAndFeel = queryResult?.data?.space?.lookAndFeel || EMPTY_LOOK_AND_FEEL;

		const childrenToRender =
			typeof children === 'function'
				? children({ isThemed: theme, isLoading: queryResult.loading })
				: children;

		return (
			<ErrorBoundary attribution={errorAttribution}>
				<ContentScreenComponent
					spaceKey={spaceKey}
					isThemed={theme}
					isLoading={queryResult.loading}
					lookAndFeel={lookAndFeel}
					isContentView={isScreenContentView}
					contentScreenStyles={contentScreenStyles}
					isLivePage={isLivePage}
				>
					{!theme && stickyHeader ? stickyHeader : null}
					<ContentContainerComponent
						isThemed={theme}
						lookAndFeel={lookAndFeel}
						isContentView={isContainerContentView}
						isLoading={queryResult.loading}
						isLivePage={isLivePage}
						isObjectSidebarEnabled={fg('confluence_frontend_content_wrapper')}
						additionalPaddingTop={isViewPageBannerVisible ? VIEW_PAGE_DISTINCTION_BANNER_HEIGHT : 0}
					>
						{fg('confluence_frontend_content_wrapper') || isRovoButtonEnabled ? (
							<Fragment>
								<div css={contentWrapperStyles}>{childrenToRender}</div>
								{isObjectSidebarShown && pageMode === PageMode.VIEW && (
									<Fragment>
										{behavior === 'push' && <ObjectSidebarPushPlaceholder />}
										<ObjectSidebarPanel />
									</Fragment>
								)}
							</Fragment>
						) : (
							childrenToRender
						)}
					</ContentContainerComponent>
					<HighlightActionsProviderForNav4 />
				</ContentScreenComponent>
			</ErrorBoundary>
		);
	}
};
